import {
  Button,
  Card,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  TablePagination,
  Box,
  TableCell,
  Checkbox
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { ChangeEvent, Fragment, useEffect, useMemo, useState } from 'react';
import { withStyles } from '@mui/styles';
import { TableHeaderCell } from 'src/components/TableHeaderCell';
import { TableColumnCell } from 'src/components/TableColumnCell';
import EditIcon from '@mui/icons-material/Edit';
import {
  updateCarryForwardedLeave,
  getCarryForwardedLeaves
} from '../../services/CarryForwardedLeaveService';
import { useDispatch } from 'react-redux';
import { AppActions } from 'src/store/AppReducer';
import {
  CarryForwardedLeave,
  filterForCarryForwardedLeave
} from '../../models/carryForwardedLeave';
import { getAllBillingRoles } from 'src/services/employeeService';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import moment from 'moment';
import ConfirmationModal from '../common/confirmationModal/ConfirmationModal';
import { useAppSelector } from 'src/hooks/hooks';
import { useForm } from 'react-hook-form';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { DatePicker } from '@mui/lab';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import { getProjectsInternal } from 'src/services/projectDetailService';
import BulkUpdate from './BulkUpdate';

const CarryForwardedLeavesView = () => {
  const dispatch = useDispatch();
  const { isLoading } = useAppSelector((state) => state.appReducer);

  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('');
  const [snackBarMessage, setSnackBarMessage] = useState('');

  const [toggleFilter, setToggleFilter] = useState<boolean>(false);
  const [carryForwardedLeaves, setCarryForwardedLeaves] = useState<
    CarryForwardedLeave[]
  >([]);
  const [sortEmployeeName, setSortEmployeeName] = useState('asc');
  const [sortDesignation, setSortDesignation] = useState('asc');
  const [sortEmployeeNamePrevious, setSortEmployeeNamePrevious] =
    useState('desc');
  const [sortDesignationPrevious, setSortDesignationPrevious] = useState('asc');
  const [selectedIndex, setSelectedIndex] = useState<number | string>();
  const [searchText, setSearchText] = useState('');
  const [selectedCarryForwardLeave, setSelectedCarryForwardLeave] = useState<
    number | string
  >();
  const [selectedExpiredDate, setSelectedExpireDate] = useState<string>('');
  const [openConformation, setOpenConformation] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<CarryForwardedLeave>();
  const [billingRoles, setBillingRoles] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [isDisabled, setIsDisable] = useState(true);
  const [selectedDesignation, setSelectedDesignation] = useState<string>('');
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedResourceType, setSelectedResourceType] = useState<string>('');
  const [selectedFormDate, setSelectedFormDate] = useState<string>('');
  const [selectedToDate, setSelectedToDate] = useState<string>('');
  const [isSelectToDate, setIsSelectToDate] = useState('');
  const [isSelectFromDate, setIsSelectFromDate] = useState('');
  const [mainSearch, setMainSearch] = useState('');
  const [isDisabledClearBtn, setIsDisabledClearBtn] = useState<boolean>(true);
  const [expiredDateError, setExpireDateError] = useState<string>('');
  const [carryForwardedAnnualLeavesError, setCarryForwardedAnnualLeavesError] =
    useState<string>('');

  // custom table row with different color
  const CustomTableRow = withStyles({
    root: {
      background: '#E6E9FF !important',
      height: '3.5rem'
    }
  })(TableRow);

  //custom table body style
  const tableBodyStyle = {
    fontSize: '11px',
    paddingBottom: '0.25rem',
    paddingTop: '0.5rem',
    color: 'text.primary'
  };

  //set frontend pagination
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  const handlePageChange = (event: any, newPage: number): void => {
    setPage(newPage);
  };

  const handleLimitChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setPage(0);
    setLimit(parseInt(event.target.value));
  };

  useEffect(() => {
    if (mainSearch) {
      const makeApiCall = async () => {
        setPage(0);
        getCarryForwardedAnnualLeaves(false);
      };

      const triggerCall = setTimeout(() => {
        makeApiCall();
      }, 500);

      return () => clearTimeout(triggerCall);
    }
  }, [mainSearch]);

  useEffect(() => {
    if (!mainSearch) {
      getCarryForwardedAnnualLeaves(false);
    }
  }, [mainSearch, sortEmployeeName, sortDesignation, page, limit]);

  const containsText = (text, searchText) =>
    text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

  //set carry forwarded annual leaves dropdown values
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dropDownValueCarryForwardedLeaves = [
    { item: 0, value: 0 },
    { item: 1, value: 1 },
    { item: 2, value: 2 },
    { item: 3, value: 3 },
    { item: 4, value: 4 },
    { item: 5, value: 5 },
    { item: 6, value: 6 },
    { item: 7, value: 7 },
    { item: 8, value: 8 },
    { item: 9, value: 9 },
    { item: 10, value: 10 },
    { item: 11, value: 11 },
    { item: 12, value: 12 },
    { item: 13, value: 13 },
    { item: 14, value: 14 }
  ];

  const memoizedFilteredOptions = useMemo(() => {
    return carryForwardedLeaves?.map((item) => {
      return dropDownValueCarryForwardedLeaves.filter(
        (option) =>
          option.value <= item.remainingAnnualLeaves &&
          containsText(option.item.toString(), searchText)
      );
    });
  }, [
    searchText,
    dropDownValueCarryForwardedLeaves,
    carryForwardedLeaves
  ]);

  //set designation
  useEffect(() => {
    getAllBillingRoles().then((res) => setBillingRoles(res.data));
    getProjectsInternal().then((res) => setProjectList(res.data));
  }, []);

  const displayedDesignation = useMemo(
    () =>
      billingRoles?.filter((option) => containsText(option.name, searchText)),
    [searchText, billingRoles]
  );

  const displayedProject = useMemo(
    () =>
      projectList?.filter((option) => containsText(option.name, searchText)),
    [searchText, projectList]
  );

  //set resource type dropdown values
  const resourceTypeValue = ['Billable', 'Non-Billable', 'Shadow', 'Support', 'T&M'];
  const [projectExpand, setProjectExpand] = useState({
    id: '',
    projectExpand: false
  });
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const displayResourceType = useMemo(
    () =>
      resourceTypeValue?.filter((option) => containsText(option, searchText)),
    [searchText, resourceTypeValue]
  );

  //clear selected item
  const onClearSelectedFilterItem = (isGetApiCall: boolean) => {
    setSelectedDesignation('');
    setSelectedResourceType('');
    setSelectedFormDate('');
    setSelectedToDate('');
    setIsSelectToDate('');
    setIsSelectFromDate('');
    setSelectedProject('');

    if (isGetApiCall) {
      getApiCall();
    }
  };

  const validateConformation = (item?: CarryForwardedLeave) => {
    if (
      selectedExpiredDate ||
      (item?.expiredDate &&
        Number(item?.remainingAnnualLeaves) >=
          Number(
            selectedCarryForwardLeave
              ? selectedCarryForwardLeave
              : item?.carryForwardLeaves
          ))
    ) {
      setOpenConformation(!openConformation);
    }
    if (!(selectedExpiredDate || item?.expiredDate)) {
      setExpireDateError('Expire Date is required.');
    }
    if (
      Number(item?.remainingAnnualLeaves) <
      Number(
        selectedCarryForwardLeave
          ? selectedCarryForwardLeave
          : item?.carryForwardLeaves
      )
    ) {
      setCarryForwardedAnnualLeavesError(
        `Remaining Annual Leave count is ${item?.remainingAnnualLeaves}`
      );
    }
  };

  const noToggleConformation = () => {
    setOpenConformation(!openConformation);
  };

  //update carry forwarded annual leave
  const onEditSelectedItem = (item: CarryForwardedLeave) => {
    const editItem = {
      id: item.id,
      employeeId: item.employeeId,
      employeeName: item.employeeName,
      designation: item.designation,
      eligible_leave_id: item.eligible_leave_id,
      remainingAnnualLeaves: item.remainingAnnualLeaves,
      carryForwardLeaves:
        selectedCarryForwardLeave === 0
          ? selectedCarryForwardLeave
          : selectedCarryForwardLeave
          ? selectedCarryForwardLeave
          : item?.carryForwardLeaves,
      expiredDate: selectedExpiredDate ? selectedExpiredDate : item.expiredDate,
      projectAllocationsDto: item.projectAllocationsDto
    };

    dispatch(AppActions.showLoader());
    updateCarryForwardedLeave(editItem)
      .then((res) => {
        console.log(res);
        if(res?.data?.statusCode === 500){
          setSnackBarMessage(res?.data?.message ? res?.data?.message : 'Unable to update carry forwarded annual leave.');
          setSnackbarSeverity('error');
          setSnackBarOpen(true);
          setOpenConformation(false);
        } else{
          setSnackBarMessage(
            'Carry forward leave has updated to the employee successfully.'
          );
          setSnackbarSeverity('success');
          setSnackBarOpen(true);
          setOpenConformation(false);
          onClearSelectedFilterItem(false);
          setSelectedIndex('');
          setExpireDateError('');
          setCarryForwardedAnnualLeavesError('');
        }
        dispatch(AppActions.hideLoader());
      })
      .catch((e) => {
        dispatch(AppActions.hideLoader());
        setSnackBarMessage('Unable to update carry forwarded annual leave.');
        setSnackbarSeverity('error');
        setSnackBarOpen(true);
        setOpenConformation(false);
      });
  };

  //set disabled filter button
  const filterMandatoryFled = (e) => {
    if (e) {
      selectedFormDate
        ? selectedToDate
          ? setIsDisable(false)
          : setIsDisable(true)
        : selectedToDate
        ? selectedFormDate
          ? setIsDisable(false)
          : setIsDisable(true)
        : setIsDisable(false);
    }
  };

  //filter option
  const { getValues } = useForm<filterForCarryForwardedLeave>();

  const modifiedString = (clearFilters: boolean) => {
    let filter = getValues();

    if (clearFilters) {
      filter = {
        isEmpty: true,
        mainSearchParam: '',
        designation: '',
        resourceType: '',
        allocationStartDate: '',
        allocationEndDate: ''
      };
    } else {
      filter.isEmpty = true;

      if (mainSearch) {
        setPage(0);
        filter.mainSearchParam = mainSearch;
        filter.isEmpty = false;
      }

      if (selectedDesignation) {
        setPage(0);
        filter.designation = selectedDesignation;
        filter.isEmpty = false;
      }

      if (selectedProject) {
        setPage(0);
        filter.projectName = selectedProject;
        filter.isEmpty = false;
      }

      if (selectedResourceType) {
        setPage(0);
        filter.resourceType = selectedResourceType;
        filter.isEmpty = false;
      }

      if (selectedFormDate) {
        setPage(0);
        filter.allocationStartDate =
          moment(selectedFormDate).format('DD/MM/YYYY');
        filter.isEmpty = false;
      }

      if (selectedToDate) {
        setPage(0);
        filter.allocationEndDate = moment(selectedToDate).format('DD/MM/YYYY');
        filter.isEmpty = false;
      }
    }

    if (!filter.isEmpty) {
      return JSON.stringify(filter)
        .replace('"isEmpty":false,', '')
        .replace('{', '')
        .replace('}', '')
        .replace(/\"/g, '');
    } else {
      return null;
    }
  };

  //sort option
  const modifiedStringSort = () => {
    let sort = getValues();
    sort.isEmpty = true;

    if (sortEmployeeName !== sortEmployeeNamePrevious) {
      sort.employeeName = sortEmployeeName;
      sort.isEmpty = false;
    }

    if (sortDesignation !== sortDesignationPrevious) {
      sort.designation = sortDesignation;
      sort.isEmpty = false;
    }

    if (!sort.isEmpty) {
      return JSON.stringify(sort)
        .replace('"isEmpty":false,', '')
        .replace('{', '')
        .replace('}', '')
        .replace(/\"/g, '');
    } else {
      return null;
    }
  };

  //get carryForwardedAnnualLeaves
  const getCarryForwardedAnnualLeaves = (isClearFilters: boolean) => {
    dispatch(AppActions.showLoader());
    const filter = modifiedString(isClearFilters);
    const sort = modifiedStringSort();

    getCarryForwardedLeaves(mainSearch ? 0 : page, limit, filter, sort)
      .then((res) => {
        if (res.data.statusCode === 200) {
          const data = res.data?.data;
          setTotalCount(data.dataCount);
          setCarryForwardedLeaves(data.carryForwardedAnnualLeaveDto);
          setSortEmployeeNamePrevious(sortEmployeeName);
          setSortDesignationPrevious(sortDesignation);
          dispatch(AppActions.hideLoader());
        }
      })
      .catch((e) => {
        dispatch(AppActions.hideLoader());
      });
  };

  const getApiCall = () => {
    getCarryForwardedAnnualLeaves(true);
  };

  const handleChildPropData = (data) => {
    if (data) {
      setSelectedIndexes([]);
      getCarryForwardedAnnualLeaves(true);
    }
  };

  const handleProjectExpand = (expandEmployeeId: string) => {
    setProjectExpand((prevState) => ({
      id: expandEmployeeId,
      projectExpand:
        prevState.id !== expandEmployeeId || !prevState.projectExpand
    }));
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelectedIndexes = carryForwardedLeaves.map(
        (_, index) => index
      );
      setSelectedIndexes(newSelectedIndexes);
      setSelectAll(true);
    } else {
      setSelectedIndexes([]);
      setSelectAll(false);
    }
  };

  const handleCheckboxClick = (event, index) => {
    const selectedIndex = selectedIndexes.indexOf(index);
    let newSelectedIndexes = [];

    if (selectedIndex === -1) {
      newSelectedIndexes = newSelectedIndexes.concat(selectedIndexes, index);
    } else if (selectedIndex === 0) {
      newSelectedIndexes = newSelectedIndexes.concat(selectedIndexes.slice(1));
    } else if (selectedIndex === selectedIndexes.length - 1) {
      newSelectedIndexes = newSelectedIndexes.concat(
        selectedIndexes.slice(0, -1)
      );
    } else if (selectedIndex > 0) {
      newSelectedIndexes = newSelectedIndexes.concat(
        selectedIndexes.slice(0, selectedIndex),
        selectedIndexes.slice(selectedIndex + 1)
      );
    }

    setSelectedIndexes(newSelectedIndexes);
    setSelectAll(
      newSelectedIndexes.length === carryForwardedLeaves.length
    );
  };

  const [openBulkUpdateToggle, setOpenBulkUpdateToggle] =
    useState<boolean>(false);

  const openBulkUpdateModel = () => {
    setOpenBulkUpdateToggle(!openBulkUpdateToggle);
  };

  const stickyColumnStyle: React.CSSProperties = {
    position: 'sticky',
    left: 0,
    zIndex: 1
  };

  return (
    <>
      <Grid>
        <Grid>
          <Typography
            style={{
              marginBottom: '1.5rem',
              fontWeight: '700',
              fontSize: '16px',
              fontFamily:
                'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"'
            }}
          >
            Carry Forwarded Annual Leaves
          </Typography>
          <Card>
            <Grid
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              sx={{ margin: '1rem 2rem' }}
            >
              <Grid>
                <TextField
                  id="mainSearchCarryForwardedLeaves"
                  sx={{ mr: 2 }}
                  size="small"
                  placeholder="Employee Name and Designation"
                  onChange={(e) => {
                    setMainSearch(e.target.value);
                  }}
                  InputProps={{
                    style: { fontSize: '12px', height: '1.75rem', width: 240 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon
                          sx={{ color: '#d3d3d3' }}
                          style={{ fontSize: '15px' }}
                        />
                      </InputAdornment>
                    )
                  }}
                />
                <Button
                  id="btn-setToggleFilter"
                  sx={{
                    mt: { xs: 2, md: 0 },
                    color: `${!toggleFilter && 'gray'}`
                  }}
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={() => {
                    setToggleFilter(!toggleFilter);
                    onClearSelectedFilterItem(false);
                    setIsDisable(true);
                    setIsDisabledClearBtn(true);
                  }}
                  style={{ height: '1.75rem', fontSize: '12px' }}
                >
                  Filters
                </Button>
              </Grid>
              <Grid>
                <Button
                  disabled={selectedIndexes.length < 1 ? true : false}
                  sx={{
                    fontSize: '11px',
                    fontWeight: 'normal'
                  }}
                  variant="contained"
                  style={{ height: '2rem', width: '8rem' }}
                  onClick={() => {
                    openBulkUpdateModel();
                  }}
                >
                  Bulk Update
                </Button>
              </Grid>
            </Grid>
            {toggleFilter && (
              <Grid
                style={{
                  marginTop: '1rem',
                  marginLeft: '2rem'
                }}
              >
                <Grid style={{ display: 'flex' }}>
                  <Grid xs={3} sx={{ mr: 2 }}>
                    <InputLabel
                      style={{
                        fontSize: '12px'
                      }}
                    >
                      Designation
                    </InputLabel>
                    <FormControl fullWidth>
                      <Select
                        id="selectedDesignation"
                        MenuProps={{ autoFocus: false }}
                        style={{
                          fontSize: '12px',
                          height: '2.3rem',
                          marginTop: '0.5rem'
                        }}
                        size="small"
                        inputProps={{ 'aria-label': 'Without label' }}
                        displayEmpty
                        defaultValue=""
                        value={selectedDesignation || ''}
                        onChange={(e) => {
                          setSelectedDesignation(e.target.value);
                          filterMandatoryFled(e.target.value);
                          setIsDisabledClearBtn(false);
                        }}
                        onClose={() => setSearchText('')}
                      >
                        <ListSubheader>
                          <TextField
                            id="searchSelectedDesignation"
                            size="small"
                            autoFocus
                            placeholder="Type to search..."
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              )
                            }}
                            onChange={(e) => {
                              setSearchText(e.target.value);
                              setIsDisabledClearBtn(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key !== 'Escape') {
                                e.stopPropagation();
                              }
                            }}
                          ></TextField>
                        </ListSubheader>
                        <MenuItem
                          disabled
                          selected
                          style={{ display: 'none' }}
                          value={''}
                        >
                          Select Designation
                        </MenuItem>
                        {displayedDesignation.map((item, i) => {
                          return (
                            <MenuItem value={item.name} key={i}>
                              {item.name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={3} sx={{ mr: 2 }}>
                    <InputLabel
                      style={{
                        fontSize: '12px'
                      }}
                    >
                      Project
                    </InputLabel>
                    <FormControl fullWidth>
                      <Select
                        id="selectedProject"
                        MenuProps={{ autoFocus: false }}
                        style={{
                          fontSize: '12px',
                          height: '2.3rem',
                          marginTop: '0.5rem'
                        }}
                        size="small"
                        inputProps={{ 'aria-label': 'Without label' }}
                        displayEmpty
                        defaultValue=""
                        value={selectedProject || ''}
                        onChange={(e) => {
                          setSelectedProject(e.target.value);
                          filterMandatoryFled(e.target.value);
                          setIsDisabledClearBtn(false);
                        }}
                        onClose={() => setSearchText('')}
                      >
                        <ListSubheader>
                          <TextField
                            id="searchSelectedProject"
                            size="small"
                            autoFocus
                            placeholder="Type to search..."
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              )
                            }}
                            onChange={(e) => {
                              setSearchText(e.target.value);
                              setIsDisabledClearBtn(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key !== 'Escape') {
                                e.stopPropagation();
                              }
                            }}
                          ></TextField>
                        </ListSubheader>
                        <MenuItem
                          disabled
                          selected
                          style={{ display: 'none' }}
                          value={''}
                        >
                          Select Project
                        </MenuItem>
                        {displayedProject.map((item, i) => {
                          return (
                            <MenuItem value={item.name} key={i}>
                              {item.name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={3} sx={{ mr: 2 }}>
                    <InputLabel
                      style={{
                        fontSize: '12px'
                      }}
                    >
                      Allocation Type
                    </InputLabel>
                    <FormControl fullWidth>
                      <Select
                        id="selectedResourceType"
                        MenuProps={{ autoFocus: false }}
                        style={{
                          fontSize: '12px',
                          height: '2.3rem',
                          marginTop: '0.5rem'
                        }}
                        size="small"
                        inputProps={{ 'aria-label': 'Without label' }}
                        displayEmpty
                        defaultValue=""
                        value={selectedResourceType || ''}
                        onChange={(e) => {
                          setSelectedResourceType(e.target.value);
                          filterMandatoryFled(e.target.value);
                          setIsDisabledClearBtn(false);
                        }}
                        onClose={() => setSearchText('')}
                      >
                        <ListSubheader>
                          <TextField
                            id="searchSelectedResourceType"
                            size="small"
                            autoFocus
                            placeholder="Type to search..."
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              )
                            }}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key !== 'Escape') {
                                e.stopPropagation();
                              }
                            }}
                          ></TextField>
                        </ListSubheader>
                        <MenuItem
                          disabled
                          selected
                          style={{ display: 'none' }}
                          value={''}
                        >
                          Select Resource Type
                        </MenuItem>
                        {displayResourceType?.map((element, i) => (
                          <MenuItem key={i} value={element}>
                            {element}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid xs={3} sx={{ mr: 2 }}>
                    <InputLabel
                      style={{
                        fontSize: '12px',
                        marginBottom: '0.5rem'
                      }}
                    >
                      Allocation Start Date
                    </InputLabel>
                    <FormControl fullWidth>
                      <DatePicker
                        InputProps={{
                          className: 'date-picker'
                        }}
                        renderInput={(params) => (
                          <TextField {...params} id="selectedFromDate" />
                        )}
                        value={selectedFormDate ? selectedFormDate : null}
                        onChange={(newValue) => {
                          const fDate = moment(newValue);
                          setSelectedFormDate(fDate.format('YYYY-MM-DD'));
                          if (newValue) {
                            setIsSelectFromDate('');
                            setIsDisabledClearBtn(false);
                            setSelectedToDate('');
                            if (!selectedToDate) {
                              setIsDisable(true);
                              setIsSelectToDate('To date is mandatory field');
                            } else {
                              setIsDisable(false);
                            }
                          }
                        }}
                      />
                    </FormControl>
                    {isSelectFromDate && (
                      <>
                        <Typography
                          style={{
                            fontSize: '11px'
                          }}
                          color="red"
                        >
                          {isSelectFromDate}
                        </Typography>
                      </>
                    )}
                  </Grid>
                  <Grid xs={3} sx={{ mr: 2 }}>
                    <InputLabel
                      style={{
                        fontSize: '12px',
                        marginBottom: '0.5rem'
                      }}
                    >
                      Allocation End Date
                    </InputLabel>
                    <FormControl fullWidth>
                      <DatePicker
                        InputProps={{
                          className: 'date-picker'
                        }}
                        renderInput={(params) => (
                          <TextField {...params} id="selectedToDate" />
                        )}
                        disabled={!selectedFormDate}
                        minDate={new Date(selectedFormDate)}
                        value={selectedToDate ? selectedToDate : null}
                        onChange={(newValue) => {
                          const tDate = moment(newValue);
                          setSelectedToDate(tDate.format('YYYY-MM-DD'));
                          if (newValue) {
                            setIsSelectToDate('');
                            setIsDisabledClearBtn(false);
                            setIsDisable(false);
                          }
                        }}
                      />
                    </FormControl>
                    {isSelectToDate && (
                      <>
                        <Typography
                          style={{
                            fontSize: '11px'
                          }}
                          color="red"
                        >
                          {isSelectToDate}
                        </Typography>
                      </>
                    )}
                  </Grid>
                </Grid>
                <Grid
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mt: 2,
                    mr: 2,
                    mb: 2
                  }}
                >
                  <Button
                    id="btn-onClearSelectedFilterItem"
                    disabled={isDisabledClearBtn}
                    sx={{ mt: { xs: 2, md: 0 }, mr: 1 }}
                    style={{ height: '1.75rem', fontSize: '12px' }}
                    variant="text"
                    onClick={() => {
                      onClearSelectedFilterItem(true);
                      setIsDisable(true);
                      setIsDisabledClearBtn(true);
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    id="btn-getCarryForwardedAnnualLeaves"
                    disabled={isDisabled}
                    sx={{ mt: { xs: 2, md: 0 } }}
                    variant="outlined"
                    type="button"
                    style={{ height: '1.75rem', fontSize: '12px' }}
                    onClick={() => {
                      getCarryForwardedAnnualLeaves(false);
                    }}
                  >
                    Filter
                  </Button>
                </Grid>
              </Grid>
            )}
            <Grid sx={{ mt: 2 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <CustomTableRow>
                      <TableCell padding="checkbox" style={stickyColumnStyle}>
                        <Checkbox
                          indeterminate={
                            selectedIndexes.length > 0 &&
                            selectedIndexes.length <
                            carryForwardedLeaves.length
                          }
                          checked={selectAll}
                          onChange={handleSelectAllClick}
                        />
                      </TableCell>
                      <TableHeaderCell
                        content="Employee Name"
                        smallFont
                        sort
                        setSortType={setSortEmployeeName}
                        sortType={sortEmployeeName}
                        leftSticky2
                      />
                      <TableHeaderCell
                        content="Designation"
                        sort
                        smallFont
                        setSortType={setSortDesignation}
                        sortType={sortDesignation}
                      />
                      <TableHeaderCell content="Project Name" smallFont />
                      <TableHeaderCell content="Allocation Type" smallFont />
                      <TableHeaderCell
                        content="Allocation Percentage"
                        smallFont
                      />
                      <TableHeaderCell
                        content="Remaining Annual Leaves"
                        smallFont
                      />
                      <TableHeaderCell
                        content="Carry Forwarded Leaves"
                        smallFont
                      />
                      <TableHeaderCell
                        content="Utilized"
                        smallFont
                      />
                      <TableHeaderCell content="Expired date" smallFont />
                      <TableHeaderCell sticky content="Actions" smallFont />
                    </CustomTableRow>
                  </TableHead>
                  <TableBody>
                    {carryForwardedLeaves?.length > 0 &&
                      carryForwardedLeaves.map((item, i) => {
                        return (
                          <Fragment key={i}>
                            <TableRow hover style={{ verticalAlign: 'top' }}>
                              <TableColumnCell sticky borderBottom>
                                <Checkbox
                                  checked={selectedIndexes.indexOf(i) !== -1}
                                  onChange={(event) =>
                                    handleCheckboxClick(event, i)
                                  }
                                />
                              </TableColumnCell>
                              <TableColumnCell leftSticky2 borderBottom>
                                <Typography
                                  variant="body2"
                                  style={tableBodyStyle}
                                  gutterBottom
                                  noWrap
                                >
                                  {item?.employeeName
                                    ? item.employeeName
                                    : '---'}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  variant="body2"
                                  style={tableBodyStyle}
                                  gutterBottom
                                  noWrap
                                >
                                  {item?.designation ? item.designation : '---'}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  <div
                                    className="border-0"
                                    style={{ width: '30px' }}
                                  >
                                    {item?.projectAllocationsDto.length > 1 && (
                                      <Button
                                        sx={{
                                          color: '#223354',
                                          backgroundColor: 'white',
                                          mt: { xs: 2, md: 0 },
                                          fontSize: '11px',
                                          fontWeight: 'normal',
                                          width: '14px',
                                          height: '14px',
                                          padding: 0,
                                          minWidth: 'unset',
                                          borderRadius: '2px',
                                          cursor: 'pointer',
                                          '&:hover': {
                                            backgroundColor: 'white',
                                            color: '#223354'
                                          }
                                        }}
                                        onClick={(e) =>
                                          handleProjectExpand(item?.employeeId)
                                        }
                                        variant="contained"
                                        startIcon={
                                          projectExpand?.id ===
                                            item?.employeeId &&
                                          projectExpand?.projectExpand ? (
                                            <HorizontalRuleIcon
                                              fontSize="small"
                                              style={{
                                                fontSize: '14px',
                                                background: '#5569FF',
                                                color: 'white'
                                              }}
                                            />
                                          ) : (
                                            <AddTwoToneIcon
                                              fontSize="small"
                                              style={{
                                                fontSize: '14px',
                                                background: '#5569FF',
                                                color: 'white'
                                              }}
                                            />
                                          )
                                        }
                                      />
                                    )}
                                  </div>
                                  <div className="border-0">
                                    <Typography
                                      marginBottom={0}
                                      paddingBottom="0.25rem"
                                      paddingTop="0.5rem"
                                      variant="body2"
                                      style={{ fontSize: '11px' }}
                                      color="text.primary"
                                      gutterBottom
                                      noWrap
                                    >
                                      {item?.projectAllocationsDto && item?.projectAllocationsDto.length > 0
                                        ? item?.projectAllocationsDto.length === 1
                                          ? item?.projectAllocationsDto[0]?.projectName
                                          : 'Multiple Projects'
                                        : '-'}
                                    </Typography>
                                  </div>
                                </div>
                              </TableColumnCell>
                              <TableColumnCell>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  <div className="border-0">
                                    <Typography
                                      marginBottom={0}
                                      paddingBottom="0.25rem"
                                      paddingTop="0.5rem"
                                      variant="body2"
                                      style={{ fontSize: '11px' }}
                                      color="text.primary"
                                      gutterBottom
                                      noWrap
                                    >
                                      {item?.projectAllocationsDto && item?.projectAllocationsDto.length > 0
                                        ? item?.projectAllocationsDto.length === 1
                                          ? item?.projectAllocationsDto[0]?.type
                                          : '-'
                                        : '-'}
                                    </Typography>
                                  </div>
                                </div>
                              </TableColumnCell>
                              <TableColumnCell>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  <div className="border-0">
                                    <Typography
                                      marginBottom={0}
                                      paddingBottom="0.25rem"
                                      paddingTop="0.5rem"
                                      variant="body2"
                                      style={{ fontSize: '11px' }}
                                      color="text.primary"
                                      gutterBottom
                                      noWrap
                                    >
                                      {item?.projectAllocationsDto && item?.projectAllocationsDto.length > 0
                                        ? item?.projectAllocationsDto.length === 1
                                          ? item?.projectAllocationsDto[0]?.allocation + '%'
                                          : '-'
                                        : '-'}
                                    </Typography>
                                  </div>
                                </div>
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  variant="body2"
                                  style={tableBodyStyle}
                                  gutterBottom
                                  noWrap
                                >
                                  {item?.remainingAnnualLeaves}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                {i === selectedIndex ? (
                                  <>
                                    <Select
                                      id={'selectedCarryForwardLeave' + i}
                                      style={{
                                        fontSize: '12px',
                                        height: '2.2rem',
                                        width: '8rem',
                                        marginTop: '2px'
                                      }}
                                      autoWidth
                                      size="small"
                                      value={
                                        selectedCarryForwardLeave === 0
                                          ? selectedCarryForwardLeave
                                          : selectedCarryForwardLeave
                                          ? selectedCarryForwardLeave
                                          : item?.carryForwardLeaves
                                      }
                                      defaultValue={item?.carryForwardLeaves}
                                      onClose={() => {
                                        setSearchText('');
                                      }}
                                      onChange={(e) => {
                                        setSelectedCarryForwardLeave(
                                          e.target.value
                                        );
                                        if (e.target.value) {
                                          setCarryForwardedAnnualLeavesError(
                                            ''
                                          );
                                        }
                                      }}
                                    >
                                      <ListSubheader>
                                        <TextField
                                          id={
                                            'searchSelectedCarryForwardLeave' +
                                            i
                                          }
                                          style={{ width: '10rem' }}
                                          size="small"
                                          autoFocus
                                          placeholder="Type to search..."
                                          fullWidth
                                          InputProps={{
                                            startAdornment: (
                                              <InputAdornment position="start">
                                                <SearchIcon />
                                              </InputAdornment>
                                            )
                                          }}
                                          onChange={(e) => {
                                            setSearchText(e.target.value);
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key !== 'Escape') {
                                              // Prevents autoselecting item while typing (default Select behaviour)
                                              e.stopPropagation();
                                            }
                                          }}
                                        />
                                      </ListSubheader>
                                      <MenuItem
                                        style={{ display: 'none' }}
                                        selected
                                        disabled
                                      ></MenuItem>
                                      {memoizedFilteredOptions[i].map(
                                        (element, index) => (
                                          <MenuItem
                                            key={index}
                                            value={Number(element.value)}
                                          >
                                            {element.item}
                                          </MenuItem>
                                        )
                                      )}
                                    </Select>
                                    {carryForwardedAnnualLeavesError && (
                                      <Typography
                                        style={{
                                          color: 'red',
                                          marginTop: '6px',
                                          fontSize: '11px'
                                        }}
                                      >
                                        {carryForwardedAnnualLeavesError}
                                      </Typography>
                                    )}
                                  </>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    style={tableBodyStyle}
                                    gutterBottom
                                    noWrap
                                  >
                                    {item?.carryForwardLeaves === 0
                                      ? item?.remainingAnnualLeaves === 0
                                        ? 'N/A'
                                        : 0
                                      : item?.carryForwardLeaves}
                                  </Typography>
                                )}
                              </TableColumnCell>
                              <TableColumnCell>
                                <Typography
                                  variant="body2"
                                  style={tableBodyStyle}
                                  gutterBottom
                                  noWrap
                                >
                                  {item?.utilized}
                                </Typography>
                              </TableColumnCell>
                              <TableColumnCell>
                                {i === selectedIndex ? (
                                  <>
                                    <DatePicker
                                      InputProps={{
                                        className: 'date-picker'
                                      }}
                                      minDate={new Date()}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          id={'selectedExpiredDate' + i}
                                          className="carrier-forwarded-date-picker"
                                        />
                                      )}
                                      value={
                                        selectedExpiredDate
                                          ? selectedExpiredDate
                                          : moment(item.expiredDate).format(
                                              'YYYY-MM-DD'
                                            )
                                      }
                                      onChange={(newValue) => {
                                        if (newValue) {
                                          setSelectedExpireDate(
                                            moment(newValue).format(
                                              'YYYY-MM-DD'
                                            )
                                          );
                                          setExpireDateError('');
                                        }
                                      }}
                                    />
                                    {expiredDateError && (
                                      <Typography
                                        style={{
                                          color: 'red',
                                          marginTop: '6px',
                                          fontSize: '11px'
                                        }}
                                      >
                                        {expiredDateError}
                                      </Typography>
                                    )}
                                  </>
                                ) : (
                                  <Typography
                                    variant="body2"
                                    style={tableBodyStyle}
                                    gutterBottom
                                    noWrap
                                  >
                                    {item?.expiredDate
                                      ? moment(item.expiredDate).format(
                                          'YYYY-MM-DD'
                                        )
                                      : item?.remainingAnnualLeaves === 0
                                      ? 'N/A'
                                      : 'MM/DD/YYYY'}
                                  </Typography>
                                )}
                              </TableColumnCell>
                              <TableColumnCell sticky>
                                <div
                                  style={{
                                    background: 'white'
                                  }}
                                >
                                  {i !== selectedIndex && (
                                    <Tooltip
                                      title={
                                        item?.remainingAnnualLeaves !== 0
                                          ? 'Edit'
                                          : 'This employee is not applicable for the annual leave'
                                      }
                                      placement="top"
                                    >
                                      <EditIcon
                                        id={'btn-selectedIndex' + i}
                                        onClick={() => {
                                          if (
                                            item?.remainingAnnualLeaves !== 0
                                          ) {
                                            setSelectedIndex(i);
                                            setSelectedCarryForwardLeave('');
                                            setSelectedExpireDate('');
                                          }
                                        }}
                                        style={{
                                          fontSize: '17px',
                                          marginTop: '1rem',
                                          cursor:
                                            item?.remainingAnnualLeaves !== 0
                                              ? 'pointer'
                                              : 'default',
                                          marginLeft: '0.9rem',
                                          opacity:
                                            item?.remainingAnnualLeaves !== 0
                                              ? ''
                                              : '0.5'
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  {i === selectedIndex && (
                                    <>
                                      <Tooltip title="Update" placement="top">
                                        <DoneIcon
                                          id={'btn-validateConformation' + i}
                                          onClick={() => {
                                            setSelectedItem(item);
                                            validateConformation(item);
                                          }}
                                          style={{
                                            fontSize: '17px',
                                            cursor: 'pointer',
                                            marginRight: 4,
                                            marginTop: '1rem'
                                          }}
                                        />
                                      </Tooltip>
                                      <Tooltip title="Cancel" placement="top">
                                        <CloseIcon
                                          id={'btn-close' + i}
                                          onClick={() => {
                                            setSelectedIndex('');
                                            setSelectedCarryForwardLeave('');
                                            setSelectedExpireDate('');
                                            setExpireDateError('');
                                            setCarryForwardedAnnualLeavesError(
                                              ''
                                            );
                                          }}
                                          style={{
                                            fontSize: '17px',
                                            cursor: 'pointer',
                                            marginTop: '1rem'
                                          }}
                                        />
                                      </Tooltip>
                                    </>
                                  )}
                                </div>
                              </TableColumnCell>
                            </TableRow>
                            {projectExpand?.id === item?.employeeId &&
                              projectExpand?.projectExpand &&
                              item?.projectAllocationsDto.map(
                                (project: any, index) => (
                                  <TableRow
                                    hover
                                    key={index}
                                    sx={{
                                      backgroundColor:
                                        'rgba(232, 234, 238, 0.2)',
                                      '&:hover': {
                                        backgroundColor:
                                          'rgba(232, 234, 238, 0.4)' // Optional: change color on hover
                                      }
                                    }}
                                  >
                                    <TableColumnCell></TableColumnCell>
                                    <TableColumnCell></TableColumnCell>
                                    <TableColumnCell>
                                      <Typography
                                        paddingLeft="30px"
                                        paddingBottom="0.25rem"
                                        paddingTop="0.5rem"
                                        variant="body2"
                                        style={{ fontSize: '11px' }}
                                        color="text.primary"
                                        gutterBottom
                                        noWrap
                                      >
                                        {project?.projectName}
                                      </Typography>
                                    </TableColumnCell>
                                    <TableColumnCell>
                                      <Typography
                                        paddingBottom="0.25rem"
                                        paddingTop="0.5rem"
                                        variant="body2"
                                        style={{ fontSize: '11px' }}
                                        color="text.primary"
                                        gutterBottom
                                        noWrap
                                      >
                                        {project?.type}
                                      </Typography>
                                    </TableColumnCell>
                                    <TableColumnCell>
                                      <Typography
                                        paddingBottom="0.25rem"
                                        paddingTop="0.5rem"
                                        variant="body2"
                                        style={{ fontSize: '11px' }}
                                        color="text.primary"
                                        gutterBottom
                                        noWrap
                                      >
                                        {project?.allocation}%
                                      </Typography>
                                    </TableColumnCell>
                                    <TableColumnCell></TableColumnCell>
                                    <TableColumnCell></TableColumnCell>
                                    <TableColumnCell></TableColumnCell>
                                  </TableRow>
                                )
                              )}
                          </Fragment>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
              {carryForwardedLeaves?.length === 0 && (
                <Typography
                  style={{
                    fontSize: '15px',
                    fontWeight: 'bold',
                    margin: '1rem',
                    textAlign: 'center'
                  }}
                  variant="body2"
                >
                  No Records Found
                </Typography>
              )}
            </Grid>
            <Box p={2}>
              <TablePagination
                sx={{
                  fontSize: '12px',
                  '.MuiTablePagination-displayedRows': {
                    fontSize: '12px'
                  },
                  '.MuiTablePagination-selectLabel': {
                    fontSize: '12px'
                  }
                }}
                component="div"
                count={totalCount}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleLimitChange}
                page={page}
                rowsPerPage={limit}
                rowsPerPageOptions={[10, 20, 30, 40, 50]}
              />
            </Box>
            <ConfirmationModal
              isLoading={isLoading}
              open={openConformation}
              toggle={validateConformation}
              message={
                'Are you sure you want to update carry forward leaves for this employee?'
              }
              onConfirm={() => {
                onEditSelectedItem(selectedItem);
              }}
              noToggle={noToggleConformation}
            />
          </Card>
          <SnackBar
            snackBarOpen={snackBarOpen}
            setSnackBarOpen={setSnackBarOpen}
            snackBarMessage={snackBarMessage}
            snackbarSeverity={snackbarSeverity}
            isClickContinue={handleChildPropData}
          />
          <BulkUpdate
            open={openBulkUpdateToggle}
            toggle={openBulkUpdateModel}
            selectedIndexes={selectedIndexes}
            carryForwardedLeaveList={carryForwardedLeaves}
            setSnackBarOpen={setSnackBarOpen}
            snackBarMessage={setSnackBarMessage}
            snackbarSeverity={setSnackbarSeverity}
            isClickContinue={handleChildPropData}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default CarryForwardedLeavesView;
